import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../api";
import { useAuth } from "../App";
import Avatar from "../components/Avatar";
import { useNotifs } from "../components/NotifContext";

const ROLE_CFG = {
  daf:       { label: "DAF", color: "#7C6FE0", bg: "#EEEDFE" },
  associe:   { label: "Associé", color: "#185FA5", bg: "#E6F1FB" },
  assistante:{ label: "Assistante", color: "#854F0B", bg: "#FAEEDA" },
  collab:    { label: "Collaborateur", color: "#3B6D11", bg: "#EAF3DE" },
};

function PasswordStrength({ pwd }) {
  const checks = [pwd.length >= 8, /[A-Z]/.test(pwd), /[0-9]/.test(pwd), /[^a-zA-Z0-9]/.test(pwd)];
  const score = checks.filter(Boolean).length;
  const colors = ["#A32D2D", "#854F0B", "#185FA5", "#3B6D11"];
  const labels = ["Trop court", "Faible", "Moyen", "Fort"];
  if (!pwd) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i < score ? colors[score - 1] : "#e0e0e0", transition: "background 0.3s" }} />
        ))}
      </div>
      <div style={{ fontSize: 11, color: colors[score - 1] || "#aaa" }}>{score > 0 ? labels[score - 1] : ""}</div>
    </div>
  );
}

export default function Profil() {
  const { token, user } = useAuth();
  const { push: pushNotif } = useNotifs();
  const [ancien, setAncien] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  const roleCfg = ROLE_CFG[user.role] || { label: user.role, color: "#888", bg: "#f0f0f0" };

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    Promise.all([
      axios.get(API + "/users/balance", { headers }),
      axios.get(API + "/leaves/mine", { headers }),
    ]).then(([balRes, leavesRes]) => {
      const total = parseFloat(balRes.data.cp_total) || 30;
      const poses = leavesRes.data.filter(l => l.statut === "approved" && l.type === "CP").reduce((s, l) => s + parseFloat(l.jours || 0), 0);
      setBalance({ total, poses, restant: Math.round((total - poses) * 10) / 10 });
      setLeaves(leavesRes.data.sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut)));
      setLoading(false);
    });
  }, [token]);

  const handleChangePwd = async () => {
    setPwdError("");
    if (!ancien || !nouveau || !confirm) { setPwdError("Tous les champs sont obligatoires."); return; }
    if (nouveau !== confirm) { setPwdError("Les nouveaux mots de passe ne correspondent pas."); return; }
    if (nouveau.length < 8) { setPwdError("Le mot de passe doit faire au moins 8 caractères."); return; }
    try {
      await axios.put(API + "/users/password",
        { ancien, nouveau },
        { headers: { Authorization: "Bearer " + token } }
      );
      pushNotif("✅ Mot de passe modifié avec succès !", "success");
      setAncien(""); setNouveau(""); setConfirm("");
    } catch (err) {
      setPwdError(err.response?.data?.error || "Erreur lors du changement.");
    }
  };

  const statusCfg = {
    approved: { color: "#3B6D11", bg: "#EAF3DE", icon: "✅", label: "Approuvé" },
    pending:  { color: "#854F0B", bg: "#FAEEDA", icon: "⏳", label: "En attente" },
    refused:  { color: "#A32D2D", bg: "#FCEBEB", icon: "❌", label: "Refusé" },
  };

  const pct = balance ? Math.round((balance.restant / balance.total) * 100) : 0;
  const barColor = pct <= 20 ? "#A32D2D" : pct <= 50 ? "#854F0B" : "#3B6D11";

  return (
    <div style={{ maxWidth: 680 }}>
      <h2 style={{ marginBottom: 20, fontWeight: 700, fontSize: 18 }}>👤 Mon profil</h2>

      {/* Carte identité */}
      <div style={{ background: "linear-gradient(135deg, #EEEDFE, #E6F1FB)", border: "1px solid #c5c0f5", borderRadius: 16, padding: "24px 28px", marginBottom: 16, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <Avatar prenom={user.prenom} nom={user.nom} size={64} fontSize={22} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1a1a2e" }}>{user.prenom} {user.nom}</div>
          <div style={{ fontSize: 13, color: "#777", marginTop: 2 }}>{user.email}</div>
          <span style={{ fontSize: 11, color: roleCfg.color, background: roleCfg.bg, padding: "3px 12px", borderRadius: 20, fontWeight: 700, display: "inline-block", marginTop: 8 }}>
            {roleCfg.label}
          </span>
        </div>
      </div>

      {/* Solde CP */}
      {!loading && balance && (
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 }}>💰 Solde congés payés</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
            {[
              { label: "Dotation", value: balance.total + " j", color: "#7C6FE0", bg: "#EEEDFE" },
              { label: "Posés", value: balance.poses + " j", color: "#854F0B", bg: "#FAEEDA" },
              { label: "Restants", value: balance.restant + " j", color: barColor, bg: pct <= 20 ? "#FCEBEB" : pct <= 50 ? "#FAEEDA" : "#EAF3DE" },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 10, color: k.color, opacity: 0.8, marginTop: 2 }}>{k.label}</div>
              </div>
            ))}
          </div>
          <div style={{ height: 8, background: "#f0f0f0", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ width: pct + "%", height: "100%", background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`, borderRadius: 10, transition: "width 0.5s ease" }} />
          </div>
          <div style={{ fontSize: 11, color: "#bbb", marginTop: 4, textAlign: "right" }}>{pct}% restant</div>
        </div>
      )}

      {/* Historique compact */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 }}>📋 Mes derniers congés</div>
        {leaves.length === 0
          ? <div style={{ fontSize: 13, color: "#ccc", textAlign: "center", padding: 20 }}>Aucune demande</div>
          : leaves.slice(0, 5).map(l => {
            const cfg = statusCfg[l.statut] || statusCfg.pending;
            return (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #f8f8f8" }}>
                <span style={{ fontSize: 16 }}>{cfg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{l.type} — {parseFloat(l.jours)}j</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {new Date(l.date_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} → {new Date(l.date_fin).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <span style={{ background: cfg.bg, color: cfg.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{cfg.label}</span>
              </div>
            );
          })
        }
      </div>

      {/* Export calendrier */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>📅 Export calendrier</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.6 }}>
          Abonnez votre calendrier (Google, Outlook, Apple) à vos congés approuvés. Il se met à jour automatiquement.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => window.open(API + "/leaves/ical?token=" + token)}
            style={{ background: "#EEEDFE", color: "#7C6FE0", border: "1px solid #c5c0f5", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ⬇ Télécharger (.ics)
          </button>
          <button onClick={() => {
            const url = API + "/leaves/ical?token=" + token;
            navigator.clipboard.writeText(url).then(() => pushNotif("🔗 Lien d'abonnement copié dans le presse-papiers !", "success"));
          }}
            style={{ background: "#f5f5f5", color: "#555", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            🔗 Copier le lien
          </button>
        </div>
      </div>

      {/* Changement mot de passe */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 }}>🔒 Changer mon mot de passe</div>
        {pwdError && (
          <div style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>⚠️ {pwdError}</div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Mot de passe actuel</label>
          <input type="password" value={ancien} onChange={e => setAncien(e.target.value)} style={inp}
            onFocus={e => e.target.style.borderColor = "#7C6FE0"} onBlur={e => e.target.style.borderColor = "#e0e0e0"} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Nouveau mot de passe</label>
          <input type="password" value={nouveau} onChange={e => setNouveau(e.target.value)} style={inp}
            onFocus={e => e.target.style.borderColor = "#7C6FE0"} onBlur={e => e.target.style.borderColor = "#e0e0e0"} />
          <PasswordStrength pwd={nouveau} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Confirmer le nouveau mot de passe</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inp}
            onFocus={e => e.target.style.borderColor = "#7C6FE0"} onBlur={e => e.target.style.borderColor = "#e0e0e0"} />
          {confirm && nouveau && (
            <div style={{ fontSize: 11, marginTop: 4, color: confirm === nouveau ? "#3B6D11" : "#A32D2D" }}>
              {confirm === nouveau ? "✓ Les mots de passe correspondent" : "✗ Ne correspond pas"}
            </div>
          )}
        </div>
        <button onClick={handleChangePwd}
          style={{ background: "linear-gradient(135deg, #7C6FE0, #185FA5)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(124,111,224,0.3)" }}>
          Modifier le mot de passe
        </button>
      </div>
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 5 };
const inp = { width: "100%", padding: "9px 12px", border: "1.5px solid #e0e0e0", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" };
